import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { track } from '@coursera/event-pulse/sdk';

import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useEnterpriseLearnerFlowOptimization } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseLearnerFlowOptimizationContext';
import { useEnterpriseAdminEnrollment } from 'bundles/unified-description-page-common/providers/enterprise/EnterpriseAdminEnrollmentDataProvider';

type UseEnterpriseTrackingAndNavigationProps = {
  isLoadingAutoContinue: boolean | undefined;
  resourcePath?: string | null;
};

const useEnterpriseTrackingAndNavigation = ({
  isLoadingAutoContinue,
  resourcePath,
}: UseEnterpriseTrackingAndNavigationProps) => {
  const location = useLocation();
  const { isExternal } = location.query;
  const isComingFromExternalPlatforms = isExternal === 'true';
  const { setNavigationUrl, setTrackingComplete } = useEnterpriseLearnerFlowOptimization();
  const features = useEnterpriseProductFeatures();
  const { isAdminEnrollmentEnabled: isGlobalAutoEnrollmentEnabled } = useEnterpriseAdminEnrollment();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const hasTracked = React.useRef(false);

  React.useLayoutEffect(() => {
    const trackOptimizedLearnerFlow = async (
      journeyOptimizationContext: 'optimized_level_1' | 'optimized_level_2' | 'non_optimized'
    ) => {
      await track('track_learner_journey_optimized_context', {
        journeyOptimizationContext,
        enterprise: {
          organizationSlug: thirdPartyOrganization?.slug || '',
        },
      });
      hasTracked.current = true;
    };

    const handleTrackOptimizedLearnerFlow = async () => {
      if (isComingFromExternalPlatforms && !isLoadingAutoContinue && !hasTracked.current) {
        if (isGlobalAutoEnrollmentEnabled) {
          await trackOptimizedLearnerFlow('optimized_level_2');
        } else if (features?.enableOptimizedLearnerFlow) {
          await trackOptimizedLearnerFlow('optimized_level_1');
        } else {
          await trackOptimizedLearnerFlow('non_optimized');
        }

        setTrackingComplete?.(true);
      }
      if (resourcePath) {
        setNavigationUrl?.(resourcePath);
      }
    };

    handleTrackOptimizedLearnerFlow();
  }, [
    isGlobalAutoEnrollmentEnabled,
    features?.enableOptimizedLearnerFlow,
    isComingFromExternalPlatforms,
    resourcePath,
    thirdPartyOrganization?.slug,
    setNavigationUrl,
    setTrackingComplete,
    isLoadingAutoContinue,
  ]);
};

export default useEnterpriseTrackingAndNavigation;
